// frontend/src/components/ReviewPage/useComparisonState.ts
import { z } from 'zod'
import { subYears, parseISO, formatISO } from 'date-fns'
import { useSyncedState } from '~/state/useSyncedState'
import { boolUrl } from '~/state/urlSchemas'
import { useSetMultipleSearchParams } from '~/state/useSetMultipleSearchParams'

export type ComparisonMode = 'off' | 'period' | 'metric'
const comparisonModeSchema = z.enum(['off', 'period', 'metric'])

export interface ComparisonState {
  mode: ComparisonMode
  setMode: (mode: ComparisonMode) => void
  /** Atomically enter metric mode with a chosen 2nd-metric root (avoids a two-write race). */
  setMetricCompare: (root: string) => void
  /** Derived: mode !== 'off'. Retained for existing call-sites. */
  enabled: boolean
  /** Back-compat: true → mode 'period', false → 'off'. */
  setEnabled: (b: boolean) => void
  /** The 2nd-metric kpi_root (metric mode). */
  compareKpi: string
  setCompareKpi: (root: string) => void
  fromIso: string
  toIso: string
  setRange: (fromIso: string, toIso: string) => void
}

// Subtracts exactly 1 year from both ends of the primary window.
// Leap-day handling per date-fns: 2024-02-29 minus 1 year → 2023-02-28.
function deriveYoyDefault(
  primaryFromIso: string,
  primaryToIso: string,
): { fromIso: string; toIso: string } {
  return {
    fromIso: formatISO(subYears(parseISO(primaryFromIso), 1), { representation: 'date' }),
    toIso: formatISO(subYears(parseISO(primaryToIso), 1), { representation: 'date' }),
  }
}

/**
 *  URL keys here are mirrored in useMultiTimeframeState's COMPARE_KEYS (row 22: entering
 *  multi-TF clears them) — keep both lists in sync when adding compare params.
 */
export function useComparisonState(primaryFromIso: string, primaryToIso: string): ComparisonState {
  const defaults = deriveYoyDefault(primaryFromIso, primaryToIso)
  const [modeParam] = useSyncedState('compareMode', comparisonModeSchema, 'url', 'off')
  // Legacy pre-mode URLs used ?compare=true (period). Honour it only when compareMode is absent
  // (i.e. still its 'off' default), so old shared links keep working.
  const [legacyCompare] = useSyncedState('compare', boolUrl, 'url', false)
  const mode: ComparisonMode = modeParam !== 'off' ? modeParam : legacyCompare ? 'period' : 'off'

  const [compareKpi] = useSyncedState('compareKpi', z.string(), 'url', '')
  const [fromIso] = useSyncedState('compareFrom', z.string(), 'url', defaults.fromIso)
  const [toIso] = useSyncedState('compareTo', z.string(), 'url', defaults.toIso)
  const setMultipleSearchParams = useSetMultipleSearchParams()

  const setMode = (next: ComparisonMode) =>
    // Clear legacy `compare` on any explicit write; clear compareKpi when leaving metric mode.
    setMultipleSearchParams({
      compareMode: next,
      compare: null,
      ...(next === 'metric' ? {} : { compareKpi: null }),
    })

  const setMetricCompare = (root: string) =>
    setMultipleSearchParams({ compareMode: 'metric', compareKpi: root, compare: null })

  return {
    mode,
    setMode,
    setMetricCompare,
    enabled: mode !== 'off',
    setEnabled: (b) => setMode(b ? 'period' : 'off'),
    compareKpi,
    setCompareKpi: (root) => setMultipleSearchParams({ compareKpi: root }),
    fromIso,
    toIso,
    setRange: (f, t) => setMultipleSearchParams({ compareFrom: f, compareTo: t }),
  }
}
